import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CountryPhoneInput from '../components/CountryPhoneInput';
import SearchableDropdown from '../components/SearchableDropdown';
import axios from 'axios';
import { trackFeatureUsage, trackSearch, trackFormSubmission, trackEvent } from '../utils/analytics';

const LeadManagement = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [leadFollowups, setLeadFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [followupsError, setFollowupsError] = useState('');
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [followupLead, setFollowupLead] = useState(null);
  const [followupForm, setFollowupForm] = useState({
    description: '',
    nextFollowupAt: ''
  });
  const [followupSaving, setFollowupSaving] = useState(false);
  const [followupFormError, setFollowupFormError] = useState('');
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    gender: 'MALE',
    phone: '',
    phoneCountryId: null,
    email: '',
    weightKg: '',
    heightCm: '',
    dateOfBirth: '',
    age: '',
    customerType: 'ONLINE',
    referredById: '',
    location: '',
    comment: '',
    leadSource: ''
  });
  const [referralOptions, setReferralOptions] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [convertLoading, setConvertLoading] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [detailActionError, setDetailActionError] = useState('');

  useEffect(() => {
    fetchLeads();
    fetchReferralOptions();
    
    // Track lead management page view
    trackFeatureUsage('Lead Management', 'view');
  }, [currentPage, sortField, sortDirection, searchQuery]);

  const fetchLeads = async () => {
    try {
      setSearchLoading(true);
      
      const params = {
        page: currentPage,
        size: pageSize,
        search: searchQuery,
        sortBy: sortField,
        sortDir: sortDirection
      };
      
      const response = await axios.get('/api/leads', { params });
      const data = response.data;
      
      setLeads(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setError('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchReferralOptions = async () => {
    try {
      const response = await axios.get('/api/customers/referral-options');
      setReferralOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const leadData = {
        ...newLead,
        weightKg: newLead.weightKg ? parseFloat(newLead.weightKg) : null,
        heightCm: newLead.heightCm ? parseInt(newLead.heightCm) : null,
        dateOfBirth: newLead.dateOfBirth || null,
        age: newLead.age ? parseInt(newLead.age) : null
      };

      if (editingLeadId) {
        await axios.put(`/api/leads/${editingLeadId}`, leadData);
      } else {
        await axios.post('/api/leads', leadData);
      }
      
      trackFormSubmission('add_lead_form', true);
      trackEvent('lead_added', {
        event_category: 'Lead Management',
        event_label: 'New Lead Added',
        customer_type: newLead.customerType
      });
      
      setShowAddForm(false);
      setConfirmMessage(editingLeadId ? 'Lead updated successfully!' : 'Lead added successfully!');
      setShowConfirmModal(true);
      resetForm();
      setCurrentPage(0);
      fetchLeads();
    } catch (error) {
      console.error('Failed to add lead:', error);
      trackFormSubmission('add_lead_form', false);
      setFormError(error.response?.data?.message || 'Failed to add lead. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setDetailsLoading(true);
    setFollowupsLoading(true);
    setFollowupsError('');
    setLeadFollowups([]);
    setDetailActionError('');
    setShowLeadDetails(true);
    
    const [detailsResult, followupsResult] = await Promise.allSettled([
      axios.get(`/api/leads/${lead.id}`),
      axios.get(`/api/leads/${lead.id}/followups`)
    ]);

    if (detailsResult.status === 'fulfilled') {
      setLeadDetails(detailsResult.value.data);
    } else {
      console.error('Failed to fetch lead details:', detailsResult.reason);
      setError('Failed to load lead details. Please try again.');
    }

    if (followupsResult.status === 'fulfilled') {
      setLeadFollowups(followupsResult.value.data || []);
    } else {
      console.error('Failed to fetch lead followups:', followupsResult.reason);
      setFollowupsError('Failed to load followup history. Please try again.');
    }

    setDetailsLoading(false);
    setFollowupsLoading(false);
  };

  const openEditLead = (lead) => {
    setEditingLeadId(lead.id);
    setNewLead({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      gender: lead.gender || 'OTHER',
      phone: lead.phone || '',
      phoneCountryId: lead.phoneCountry?.id || null,
      email: lead.email || '',
      weightKg: lead.weightKg ?? '',
      heightCm: lead.heightCm ?? '',
      dateOfBirth: lead.dateOfBirth || '',
      age: lead.age ?? '',
      customerType: lead.customerType || 'ONLINE',
      referredById: lead.referredBy?.id || '',
      location: lead.location || '',
      comment: lead.comment || '',
      leadSource: lead.leadSource || ''
    });
    setFormError('');
    setShowLeadDetails(false);
    setShowAddForm(true);
  };

  const openAddFollowupModal = (lead) => {
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    const localDateTime = new Date(nextHour.getTime() - nextHour.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setFollowupLead(lead);
    setFollowupForm({ description: '', nextFollowupAt: localDateTime });
    setFollowupFormError('');
    setShowAddFollowupModal(true);
  };

  const handleAddFollowup = async (event) => {
    event.preventDefault();
    if (!followupLead) return;

    setFollowupSaving(true);
    setFollowupFormError('');
    try {
      const response = await axios.post(`/api/leads/${followupLead.id}/followups`, followupForm);
      if (selectedLead?.id === followupLead.id) {
        setLeadFollowups((current) => [response.data, ...current]);
      }
      setShowAddFollowupModal(false);
      setFollowupLead(null);
      setFollowupForm({ description: '', nextFollowupAt: '' });
    } catch (error) {
      console.error('Failed to add lead followup:', error);
      setFollowupFormError(error.response?.data?.message || 'Failed to save followup. Please try again.');
    } finally {
      setFollowupSaving(false);
    }
  };

  const handleConvertToCustomer = async () => {
    if (!selectedLead) return;
    
    setConvertLoading(true);
    setDetailActionError('');
    try {
      await axios.post(`/api/leads/${selectedLead.id}/convert`);
      
      trackEvent('lead_converted', {
        event_category: 'Lead Management',
        event_label: selectedLead.fullName,
        lead_id: selectedLead.id
      });
      
      setShowLeadDetails(false);
      setConfirmMessage('Lead converted to customer successfully!');
      setShowConfirmModal(true);
      fetchLeads(); // Refresh the list
    } catch (error) {
      console.error('Failed to convert lead:', error);
      setDetailActionError(error.response?.data?.message || error.response?.data || 'Failed to convert lead to customer. Please try again.');
    } finally {
      setConvertLoading(false);
    }
  };

  const handleUpdateStatus = async (leadId, status) => {
    try {
      await axios.put(`/api/leads/${leadId}/status?status=${status}`);
      fetchLeads(); // Refresh the list
    } catch (error) {
      console.error('Failed to update lead status:', error);
      setError('Failed to update lead status. Please try again.');
    }
  };

  const handleDeleteLead = async (lead) => {
    if (lead.status === 'CONVERTED' || lead.convertedCustomerId) return;
    if (!window.confirm(`Delete ${lead.fullName || 'this lead'}? This will also remove its follow-ups and cannot be undone.`)) return;

    setDeletingLeadId(lead.id);
    setDetailActionError('');
    try {
      await axios.delete(`/api/leads/${lead.id}`);
      if (selectedLead?.id === lead.id) {
        setShowLeadDetails(false);
        setSelectedLead(null);
        setLeadDetails(null);
      }
      setConfirmMessage('Lead deleted successfully!');
      setShowConfirmModal(true);
      fetchLeads();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete lead. Please try again.';
      if (selectedLead?.id === lead.id) setDetailActionError(message);
      else setError(message);
    } finally {
      setDeletingLeadId(null);
    }
  };

  const resetForm = () => {
    setEditingLeadId(null);
    setNewLead({
      firstName: '',
      lastName: '',
      gender: 'MALE',
      phone: '',
      phoneCountryId: null,
      email: '',
      weightKg: '',
      heightCm: '',
      dateOfBirth: '',
      age: '',
      customerType: 'ONLINE',
      referredById: '',
      location: '',
      comment: '',
      leadSource: ''
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handleSearch = (query) => {
    if (query && query.length >= 2) {
      trackSearch(query, 'leads');
    }
    setSearchQuery(query);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW': return 'var(--primary-blue)';
      case 'CONVERTED': return 'var(--success)';
      case 'CLOSED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading leads...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Lead Management</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Manage your leads and convert them to customers
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => { resetForm(); setShowAddForm(true); }}
          style={{ flexShrink: 0 }}
        >
          <span className="btn-icon">➕</span>
          <span className="btn-text">Add Lead</span>
        </button>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            My Leads ({totalElements} total)
          </h3>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ margin: 0, paddingRight: searchLoading ? '40px' : '35px' }}
            />
            {searchLoading && (
              <div style={{
                position: 'absolute',
                right: '35px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--primary-blue)'
              }}>
                ⏳
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Pagination Info */}
        {totalElements > 0 && (
          <div style={{ 
            marginBottom: '15px', 
            fontSize: '14px', 
            color: 'var(--gray-dark)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} leads
              {searchQuery && ` (filtered by "${searchQuery}")`}
            </span>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        )}

        {leads.length === 0 ? (
          searchQuery ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No leads found matching "{searchQuery}".
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No leads found. Click "Add Lead" to create your first lead.
            </div>
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('fullName')}
                  >
                    Customer Name {getSortIcon('fullName')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('weightKg')}
                  >
                    Weight (kg) {getSortIcon('weightKg')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('heightCm')}
                  >
                    Height (cm) {getSortIcon('heightCm')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('email')}
                  >
                    Email {getSortIcon('email')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created Date {getSortIcon('createdAt')}
                  </th>
                  <th style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    borderBottom: '2px solid var(--light-blue)'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, index) => (
                  <tr 
                    key={lead.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {lead.fullName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {lead.weightKg ? `${lead.weightKg} kg` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {lead.heightCm ? `${lead.heightCm} cm` : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {lead.email}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getStatusColor(lead.status),
                        fontWeight: '500',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: lead.status === 'NEW' ? 'var(--pale-blue)' : 
                                        lead.status === 'CONVERTED' ? '#C6F6D5' : '#FED7D7'
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => openAddFollowupModal(lead)}
                        >
                          Add Followup
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleViewLead(lead)}
                        >
                          Details
                        </button>
                        {lead.status !== 'CONVERTED' && !lead.convertedCustomerId && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                            onClick={() => handleDeleteLead(lead)}
                            disabled={deletingLeadId === lead.id}
                          >
                            {deletingLeadId === lead.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              First
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Previous
            </button>
            
            <span style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
              Page {currentPage + 1} of {totalPages}
            </span>
            
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Next
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add Lead Form Modal */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingLeadId ? 'Edit Lead' : 'Add New Lead'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleAddLead}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="form-input"
                    maxLength="50"
                    value={newLead.firstName}
                    onChange={(e) => setNewLead({...newLead, firstName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="form-input"
                    maxLength="50"
                    value={newLead.lastName}
                    onChange={(e) => setNewLead({...newLead, lastName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="gender" className="form-label">
                  Gender *
                </label>
                <select
                  id="gender"
                  className="form-input"
                  value={newLead.gender}
                  onChange={(e) => setNewLead({...newLead, gender: e.target.value})}
                  required
                  disabled={formLoading}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number *
                  </label>
                  <CountryPhoneInput
                    phoneValue={newLead.phone}
                    countryValue={newLead.phoneCountryId}
                    onPhoneChange={(phone) => setNewLead({...newLead, phone})}
                    onCountryChange={(countryId) => setNewLead({...newLead, phoneCountryId: countryId})}
                    disabled={formLoading}
                    required={true}
                    placeholder="Enter phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address {editingLeadId ? '' : '*'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    value={newLead.email}
                    onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                    required={!editingLeadId}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="weightKg" className="form-label">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="weightKg"
                    className="form-input"
                    value={newLead.weightKg}
                    onChange={(e) => setNewLead({...newLead, weightKg: e.target.value})}
                    disabled={formLoading}
                    placeholder="e.g., 75.5"
                    min="20"
                    max="300"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="heightCm" className="form-label">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="heightCm"
                    className="form-input"
                    value={newLead.heightCm}
                    onChange={(e) => setNewLead({...newLead, heightCm: e.target.value})}
                    disabled={formLoading}
                    placeholder="e.g., 175.5"
                    min="50"
                    max="250"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="dateOfBirth" className="form-label">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    className="form-input"
                    value={newLead.dateOfBirth}
                    onChange={(e) => {
                      const dob = e.target.value;
                      setNewLead({...newLead, dateOfBirth: dob});
                      
                      // Auto-calculate age if DOB is provided
                      if (dob) {
                        const birthDate = new Date(dob);
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const monthDiff = today.getMonth() - birthDate.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                          age--;
                        }
                        setNewLead(prev => ({...prev, age: age.toString()}));
                      }
                    }}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="age" className="form-label">
                    Age (years)
                  </label>
                  <input
                    type="number"
                    id="age"
                    className="form-input"
                    value={newLead.age}
                    onChange={(e) => setNewLead({...newLead, age: e.target.value})}
                    disabled={formLoading}
                    placeholder="Auto-filled from DOB"
                    min="1"
                    max="120"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="customerType" className="form-label">
                  Customer Type *
                </label>
                <select
                  id="customerType"
                  className="form-input"
                  value={newLead.customerType}
                  onChange={(e) => setNewLead({...newLead, customerType: e.target.value})}
                  required
                  disabled={formLoading}
                >
                  <option value="ONLINE">Online</option>
                  <option value="CLUB">Club</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="referredBy" className="form-label">
                  Referred By *
                </label>
                <SearchableDropdown
                  options={[
                    { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                    ...referralOptions
                  ]}
                  value={newLead.referredById}
                  onChange={(value) => setNewLead({...newLead, referredById: value})}
                  placeholder="Search and select referrer..."
                  disabled={formLoading}
                  required={true}
                  displayKey="fullName"
                  valueKey="id"
                  emptyMessage="No referral options available"
                />
              </div>

              <div className="form-group">
                <label htmlFor="location" className="form-label">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  className="form-input"
                  value={newLead.location}
                  onChange={(e) => setNewLead({...newLead, location: e.target.value})}
                  disabled={formLoading}
                  placeholder="e.g., Mumbai, Maharashtra"
                />
              </div>

              <div className="form-group">
                <label htmlFor="leadSource" className="form-label">
                  Lead Source
                </label>
                <select
                  id="leadSource"
                  className="form-input"
                  value={newLead.leadSource}
                  onChange={(e) => setNewLead({...newLead, leadSource: e.target.value})}
                  disabled={formLoading}
                >
                  <option value="">Select Lead Source</option>
                  <option value="Afresh Party">Afresh Party</option>
                  <option value="Health Checkup Camp">Health Checkup Camp</option>
                  <option value="1-1 Invitation">1-1 Invitation</option>
                  <option value="Home Visit">Home Visit</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Flyer">Flyer</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="comment" className="form-label">
                  Comment
                </label>
                <textarea
                  id="comment"
                  className="form-input"
                  rows="3"
                  value={newLead.comment}
                  onChange={(e) => setNewLead({...newLead, comment: e.target.value})}
                  disabled={formLoading}
                  placeholder="Additional notes about the lead..."
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormError('');
                    resetForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving...' : (editingLeadId ? 'Save Changes' : 'Add Lead')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Lead Followup Modal */}
      {showAddFollowupModal && followupLead && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <div>
                <h3>Add Followup</h3>
                <div style={{ color: 'var(--gray-dark)', fontSize: '13px', marginTop: '4px' }}>
                  {followupLead.fullName}
                </div>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowAddFollowupModal(false)}
                disabled={followupSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="profile-form" onSubmit={handleAddFollowup}>
              {followupFormError && <div className="alert alert-error">{followupFormError}</div>}

              <div className="form-group">
                <label htmlFor="leadFollowupDescription" className="form-label">
                  Description / Comment *
                </label>
                <textarea
                  id="leadFollowupDescription"
                  className="form-input"
                  rows="4"
                  maxLength="5000"
                  value={followupForm.description}
                  onChange={(event) => setFollowupForm({ ...followupForm, description: event.target.value })}
                  required
                  disabled={followupSaving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="leadNextFollowupAt" className="form-label">
                  Next Followup Date & Time *
                </label>
                <input
                  id="leadNextFollowupAt"
                  type="datetime-local"
                  className="form-input"
                  value={followupForm.nextFollowupAt}
                  onChange={(event) => setFollowupForm({ ...followupForm, nextFollowupAt: event.target.value })}
                  required
                  disabled={followupSaving}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddFollowupModal(false)}
                  disabled={followupSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={followupSaving || !followupForm.description.trim() || !followupForm.nextFollowupAt}
                >
                  {followupSaving ? 'Saving...' : 'Save Followup'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead Details Modal */}
      {showLeadDetails && selectedLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '760px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                Lead Details
              </h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowLeadDetails(false);
                  setSelectedLead(null);
                  setLeadDetails(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {detailsLoading ? (
              <div className="loading">Loading lead details...</div>
            ) : leadDetails ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Personal Information</h4>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Full Name:</strong> {leadDetails.fullName}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Email:</strong> {leadDetails.email || 'Not provided'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Phone:</strong> {leadDetails.phone}
                      {leadDetails.phoneCountry && ` (${leadDetails.phoneCountry.displayName})`}
                      {leadDetails.phone && <a className="btn btn-secondary" style={{ marginLeft: '8px', padding: '3px 8px', fontSize: '11px' }} href={`tel:${leadDetails.phoneCountry ? `+${leadDetails.phoneCountry.countryCode}` : ''}${leadDetails.phone}`}>Call now</a>}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Gender:</strong> {leadDetails.gender || 'Not provided'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Date of Birth:</strong> {leadDetails.dateOfBirth ? new Date(leadDetails.dateOfBirth).toLocaleDateString() : 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Age:</strong> {leadDetails.age || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Lead Information</h4>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Weight:</strong> {leadDetails.weightKg ? `${leadDetails.weightKg} kg` : 'N/A'}
                    </div>
                    <div>
                      <strong>Target Weight:</strong> {leadDetails.targetWeightKg ? `${leadDetails.targetWeightKg} kg` : 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Height:</strong> {leadDetails.heightCm ? `${leadDetails.heightCm} cm` : 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Customer Type:</strong> {leadDetails.customerType}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Location:</strong> {leadDetails.location || 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Referred By:</strong> {leadDetails.referredBy?.fullName || 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Lead Source:</strong> {leadDetails.leadSource || 'N/A'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Status:</strong> 
                      <span style={{
                        color: getStatusColor(leadDetails.status),
                        marginLeft: '5px',
                        fontWeight: '500'
                      }}>
                        {leadDetails.status}
                      </span>
                    </div>
                  </div>
                </div>

                {leadDetails.comment && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Comment</h4>
                    <div style={{ 
                      padding: '10px', 
                      background: 'var(--gray-light)', 
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}>
                      {leadDetails.comment}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>Timeline</h4>
                  <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Created:</strong> {new Date(leadDetails.createdAt).toLocaleString()}
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      <strong>Last Updated:</strong> {new Date(leadDetails.updatedAt).toLocaleString()}
                    </div>
                    {leadDetails.convertedCustomerId && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Converted Customer ID:</strong> {leadDetails.convertedCustomerId}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--gray-medium)', paddingTop: '20px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <h4 style={{ color: 'var(--primary-blue)', margin: 0 }}>Followup History</h4>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                      onClick={() => openAddFollowupModal(leadDetails)}
                    >
                      Add Followup
                    </button>
                  </div>

                  {followupsError && <div className="alert alert-error">{followupsError}</div>}

                  {followupsLoading ? (
                    <div className="loading">Loading followup history...</div>
                  ) : leadFollowups.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '6px' }}>
                      No followups added for this lead.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '320px', overflowY: 'auto' }}>
                      {leadFollowups.map((followup) => (
                        <div
                          key={followup.id}
                          style={{
                            border: '1px solid var(--gray-medium)',
                            borderRadius: '6px',
                            padding: '14px',
                            background: 'var(--white)'
                          }}
                        >
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px', marginBottom: '10px' }}>
                            {followup.description}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', color: 'var(--gray-dark)', fontSize: '12px' }}>
                            <span>
                              <strong>Next Followup:</strong> {new Date(followup.nextFollowupAt).toLocaleString()}
                            </span>
                            <span>
                              Added {new Date(followup.createdAt).toLocaleString()} by {followup.coachName || 'Coach'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {detailActionError && <div className="alert alert-error" style={{ marginBottom: '15px' }}>{detailActionError}</div>}
                {!leadDetails.email && leadDetails.status === 'NEW' && (
                  <div className="alert alert-error" style={{ marginBottom: '15px' }}>
                    Add an email address before converting this lead to a customer.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {leadDetails.status !== 'CONVERTED' && !leadDetails.convertedCustomerId && (
                    <>
                      <button className="btn btn-secondary" onClick={() => openEditLead(leadDetails)}>
                        Edit Lead
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteLead(leadDetails)}
                        disabled={deletingLeadId === leadDetails.id}
                        style={{ color: 'var(--error)' }}
                      >
                        {deletingLeadId === leadDetails.id ? 'Deleting...' : 'Delete Lead'}
                      </button>
                    </>
                  )}
                  {leadDetails.status === 'NEW' && (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleUpdateStatus(leadDetails.id, 'CLOSED')}
                        style={{ color: 'var(--error)' }}
                      >
                        Mark as Closed
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleConvertToCustomer}
                        disabled={convertLoading}
                        style={{ 
                          background: 'var(--success)',
                          border: 'none'
                        }}
                      >
                        {convertLoading ? 'Converting...' : 'Convert To Customer'}
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowLeadDetails(false);
                      setSelectedLead(null);
                      setLeadDetails(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                Failed to load lead details.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Success!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {confirmMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadManagement;
